import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { PenSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { Button } from '@/components/Button';
import { latencySchema } from '@/validations/latencySchema';
import api from '@/services/api';
import { EditBox, EditDrawerStack } from './styles';

type LatencyFieldErrorsType = Record<
  number,
  { name?: string; address?: string }
>;

const preferenceFields: (keyof UserPreferencesType)[] = [
  'totalDevices',
  'totalOnlineDevices',
  'totalOfflineDevices',
  'totalEnabledDevices',
  'totalDisabledDevices',
  'latency',
  'manufacturers',
  'serverData',
  'cpuData',
  'memoryData',
  'diskData',
];

function EditDashboard({
  handleSuccess,
  userPreferences,
  draftPreferences,
  setDraftPreferences,
  setLatencyErrors,
}: {
  handleSuccess: (confirm: boolean) => void;
  userPreferences: UserPreferencesType;
  draftPreferences: UserPreferencesType | null;
  setDraftPreferences: (
    newDraftPreferences: UserPreferencesType | null
  ) => void;
  setLatencyErrors: (errors: LatencyFieldErrorsType) => void;
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleEdit = async (status: boolean) => {
    if (status) {
      // Validate latency addresses before saving
      try {
        // check if fields matches schema
        await latencySchema(t).validate(draftPreferences?.pingAddresses ?? [], {
          abortEarly: false,
        });
        setLatencyErrors({});
      } catch (error) {
        // If validation fails, extract the errors and set them in state
        if (error instanceof Yup.ValidationError) {
          const fieldErrors: LatencyFieldErrorsType = {};
          error.inner.forEach((fieldError) => {
            const match = fieldError.path?.match(/^\[(\d+)\]\.(name|address)$/);
            if (!match) return;
            const [, index, field] = match;
            fieldErrors[Number(index)] = {
              ...fieldErrors[Number(index)],
              [field]: fieldError.message,
            };
          });
          setLatencyErrors(fieldErrors);
          error.errors.forEach((message) =>
            enqueueSnackbar(message, { variant: 'error' })
          );
        }
        return;
      }

      try {
        await api.put('/user-preferences', draftPreferences);
        enqueueSnackbar(t('dashboard.editDashboard.success'), {
          variant: 'success',
        });
      } catch {
        enqueueSnackbar(t('dashboard.editDashboard.error'), {
          variant: 'error',
        });
        setDraftPreferences(userPreferences);
      }
    } else {
      setLatencyErrors({});
    }
    handleSuccess(status);
  };

  const handleCheckboxChange = (
    field: keyof UserPreferencesType,
    checked: boolean
  ) => {
    const updatedPreferences = {
      ...draftPreferences,
      [field]: checked,
    } as UserPreferencesType;
    setDraftPreferences(updatedPreferences);
  };

  return (
    <EditBox>
      <EditDrawerStack>
        {/** Header */}
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <PenSquare />
            <Typography variant="h4">
              {t('dashboard.editDashboard.title')}
            </Typography>
          </Stack>
          <Typography variant="caption">
            {t('dashboard.editDashboard.selectCards')}
          </Typography>
        </Stack>

        {/** Checkbox group */}
        <Stack direction="column">
          {preferenceFields.map((field) => (
            <FormControlLabel
              key={field}
              control={
                <Checkbox
                  checked={draftPreferences![field] as boolean}
                  onChange={(_, checked) =>
                    handleCheckboxChange(field, checked)
                  }
                />
              }
              label={t(`dashboard.editDashboard.fields.${field}`)}
            />
          ))}
        </Stack>

        {/** Footer */}
        <Stack sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between' }}
            spacing={2}
          >
            <Button variant="outlined" onClick={() => handleEdit(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="contained" onClick={() => handleEdit(true)}>
              {t('common.save')}
            </Button>
          </Stack>
        </Stack>
      </EditDrawerStack>
    </EditBox>
  );
}

export default EditDashboard;
